// One building's steps on real dates: done steps sit on the date they were done, and
// everything still to do starts no earlier than today, after the steps it waits on.
import type { AmazonBuilding, AmazonNeed } from "../../api/hooks/useAmazonOverview";
import type { Tone } from "../../utils/sizeGap";
import {
  addBusinessDays,
  addDays,
  addMonths,
  day,
  daysBetween,
  formatDate,
  latest,
  uncountedYears,
} from "./dates";

/** How long after an application an offer is expected when the DNO gives no date. Must
 * agree with amazon_overview.OFFER_MONTHS. */
const OFFER_MONTHS = 3;
import { achievableSize } from "./achievable";
import { designInputs, owed } from "./needs";
import { LIMITS, type Owner, STEPS, stepById } from "./pathSteps";
import { sizeText } from "./sizes";

/** done and open read from the record; planned is a step not started yet, including one
 * nothing on record says has happened. */
export type BarKind = "done" | "open" | "blocked" | "planned";

export interface Scheduled {
  id: string;
  kind: BarKind;
  /** Null for a done step with no date on record: it is left off the timeline. */
  start: Date | null;
  end: Date | null;
  /** Something it waits on is blocked. */
  waiting: boolean;
  detail: string;
  /** A grid offer still pending with no date from the DNO. */
  noDate: boolean;
  /** Why the winter rules (MSPPA s5.8) moved it, where they did. */
  winter?: string;
}

const DAYS_PER_MONTH = 30.44;

/** Two months of installation per MWp DC. */
export const installDays = (kwp: number): number =>
  (kwp / 1000) * 2 * DAYS_PER_MONTH;

export interface InstallLength {
  days: number;
  basis: string;
  /** Schedule 1's days from installation start to the target COD, where they set it. */
  scheduleGap: number | null;
}

/** How long installation takes and why: the time Schedule 1 leaves between installation
 * start and the target COD, less connecting and commissioning after it, where Legal
 * records both dates; else two months per MWp of the achievable size Step 3's tile shows,
 * the top of its range (achievable.achievableSize). With no achievable size, installation
 * has no length. */
export const installLength = (b: AmazonBuilding): InstallLength | null => {
  const start = b.contract_dates?.install;
  const after = stepById("commission").days ?? 0;
  if (start && b.committed_cod) {
    const gap = daysBetween(day(start), day(b.committed_cod));
    if (gap > after) {
      return {
        days: gap - after,
        basis: `Schedule 1's ${gap} days from installation start (${formatDate(day(start))}) to the target COD, less ${after} days to connect and commission`,
        scheduleGap: gap,
      };
    }
  }
  const kwp = achievableSize(b).kwp?.max;
  return kwp
    ? {
        days: installDays(kwp),
        basis: `2 months per MWp of ${sizeText(kwp, "kWp")} achievable`,
        scheduleGap: null,
      }
    : null;
};

/** How long the detailed design takes and whose figure that is: Design readiness's
 * estimate where it recorded one, else the page's own placeholder, said to be ours. */
export const designLength = (
  b: AmazonBuilding,
): { days: number; basis: string; ours: boolean } =>
  b.design_days
    ? { days: b.design_days.days, basis: "Design readiness's estimate", ours: false }
    : {
        days: stepById("design").days ?? 0,
        basis: "our placeholder, no estimate from Design readiness yet",
        ours: true,
      };

/** A step's planned days: the design's own length (``designLength``), else Step 1's. */
const plannedDays = (b: AmazonBuilding, id: string): number =>
  id === "design" ? designLength(b).days : (stepById(id).days ?? 0);

/** Amazon's design approval: 90 Business Days of review, then 14 to approve. */
export const DR3_BUSINESS_DAYS = 104;

/** Whether a clause reference is Amazon's spec (the EUSDR) or the contract. */
const sourceOf = (reference: string): string =>
  reference.startsWith("EUSDR") ? "the spec" : "the contract";

/** Why step ``id``'s bar is as long as it is: from the contract or the spec, naming the
 * clause, or an estimated time and whose (Noaa, 18 Oct 2026). "" for a step that is an
 * instant. */
export const lengthWhy = (b: AmazonBuilding, id: string): string => {
  if (id === "dr3") {
    return `${DR3_BUSINESS_DAYS} Business Days, from the contract (${LIMITS.dr3.reference}): ${LIMITS.dr3.clause}`;
  }
  if (id === "design") {
    const design = designLength(b);
    return `${design.days} days of design once its inputs are in: estimated time, ${design.basis}`;
  }
  if (id === "install") {
    const install = installLength(b);
    if (!install) return "No length: no achievable size recorded";
    const days = Math.round(install.days);
    return install.scheduleGap !== null
      ? `${days} days, from the contract (SPPA Schedule 1): ${install.basis}`
      : `${days} days: estimated time, ${install.basis}`;
  }
  if (id === "gridacc" && b.steps.gridacc?.due) return "Planned on the offer's accept-by date";
  const limit = LIMITS[id];
  if (limit && !limit.unbounded) {
    return `${limit.label}, from ${sourceOf(limit.reference)} (${limit.reference}): ${limit.clause}`;
  }
  const step = stepById(id);
  return step.days ? `${step.days} days: estimated time` : "";
};

/** When the forecast plans Amazon's outstanding design inputs to arrive: tomorrow, Noaa's
 * call on 18 Oct 2026, the page having no lead time from Amazon to plan on. */
export const INPUTS_ARRIVE_IN_DAYS = 1;

/** The soonest anything not yet done can happen: tomorrow, never today (Noaa, 18 Oct 2026). */
export const soonest = (now: Date): Date => addDays(now, 1);

/** When a step taking its planned time must start to finish by ``finish``: design approval
 * in Business Days, everything else in calendar days. */
export const startFor = (b: AmazonBuilding, id: string, finish: Date): Date =>
  id === "dr3"
    ? addBusinessDays(finish, -DR3_BUSINESS_DAYS)
    : addDays(finish, -lengthOf(b, id));

/** The winter rules of MSPPA s5.8 as the programme plans around them: no electrical
 * interruption from 1 October to 15 January (the grid connection, commissioning, the
 * switchgear discovery shutdown), and cranes off site by the end of 14 October.
 * Installation itself carries on. */
const winterOf = (d: Date): [Date, Date] => {
  const y = d.getUTCMonth() === 0 ? d.getUTCFullYear() - 1 : d.getUTCFullYear();
  return [new Date(Date.UTC(y, 9, 1)), new Date(Date.UTC(y + 1, 0, 15))];
};

const inside = (d: Date, [a, z]: [Date, Date]): boolean => d >= a && d <= z;

/** The first winter (1 Oct to 15 Jan) the span ``start`` to ``end`` touches, if any. */
const winterTouched = (start: Date, end: Date): [Date, Date] | null => {
  for (const w of [winterOf(start), winterOf(end)]) {
    if (start <= w[1] && end >= w[0]) return w;
  }
  const next = winterOf(new Date(Date.UTC(start.getUTCFullYear(), 9, 1)));
  return start <= next[1] && end >= next[0] ? next : null;
};

/** ``start`` and ``end`` moved where the winter rules forbid them: commissioning out of
 * the winter whole, installation's crane mobilisation out of 1 to 14 October, and its
 * switchgear discovery shutdown after the winter where the whole install falls in it. */
const outsideWinter = (
  id: string,
  start: Date,
  end: Date,
): { start: Date; end: Date; winter?: string } => {
  const length = daysBetween(start, end);
  if (id === "commission") {
    const w = winterTouched(start, end);
    if (!w) return { start, end };
    const from = addDays(w[1], 1);
    return {
      start: from,
      end: addDays(from, length),
      winter: `Moved to ${formatDate(from)}: no electrical interruption from 1 October to 15 January (MSPPA s5.8).`,
    };
  }
  if (id === "install") {
    const w = winterOf(start);
    const cranesBy = new Date(Date.UTC(w[0].getUTCFullYear(), 9, 14));
    let from = start;
    let note = "";
    if (inside(start, [w[0], cranesBy])) {
      from = addDays(cranesBy, 1);
      note = `Mobilisation moved to ${formatDate(from)}: cranes must be off site by the end of 14 October (MSPPA s5.8).`;
    }
    let to = addDays(from, length);
    const winter = winterOf(from);
    if (inside(from, winter) && inside(to, winter)) {
      to = addDays(winter[1], 1);
      note = `${note ? `${note} ` : ""}Runs to ${formatDate(to)}: the switchgear discovery shutdown cannot fall between 1 October and 15 January (MSPPA s5.8).`;
    }
    return note ? { start: from, end: to, winter: note } : { start, end };
  }
  return { start, end };
};

export interface Projection {
  /** Null where neither the plan nor the specialists give a COD. */
  date: Date | null;
  /** The specialists' achievable COD set it, being later than the page's plan. */
  bySpecialists: boolean;
  /** The page's own plan; null with nothing to time installation by. */
  plan: Date | null;
}

/** The projected COD: the page's plan, or the specialists' achievable COD where that is
 * later (a date the counterparties have given can only push it back), so the page never
 * calls a building on time that the specialists say will be late. */
export const projectCod = (b: AmazonBuilding, planEnd: Date | null): Projection => {
  const plan = installLength(b) ? planEnd : null;
  const theirs = b.achievable_cod ? day(b.achievable_cod) : null;
  return theirs && (!plan || theirs > plan)
    ? { date: theirs, bySpecialists: true, plan }
    : { date: plan, bySpecialists: false, plan };
};

/** How late a projected COD reads, from its days past the committed one: amber under 14
 * days, red from there (Noaa, 19 Oct 2026). */
export const slipTone = (slip: number): Tone => (slip < 14 ? "amber" : "red");

/** A building with a state for every step: an API deployed before a step existed sends
 * none for it, and the page reads every step it draws. */
export const withEveryStep = (b: AmazonBuilding): AmazonBuilding =>
  STEPS.every((s) => b.steps[s.id])
    ? b
    : {
        ...b,
        steps: Object.fromEntries(
          STEPS.map((s) => [
            s.id,
            b.steps[s.id] ?? {
              state: "not_tracked",
              date: "",
              due: "",
              detail: "",
              kwp: null,
              kw_ac: null,
              estimated: false,
            },
          ]),
        ),
      };

/** What each scheduled step waits on, to start and to finish, and who owns it, as Step 1
 * shows them. */
export type Links = ReadonlyMap<string, { deps: string[]; endDeps: string[]; owner: Owner }>;

/** Each step's links as Step 1 shows them: the shared blueprint's where someone edited
 * them, else the page's own. A step no building has a record of (one added on the page)
 * or one removed from Step 1 cannot hold anything up by itself, so a link to it is passed
 * through to the steps it waits on: a chain through it still holds. */
export const programmeLinks = (
  edited: readonly {
    id: string;
    deps: string[];
    endDeps?: string[];
    hidden?: boolean;
    owner?: Owner;
  }[] = [],
): Links => {
  const byId = new Map(edited.map((s) => [s.id, s]));
  const dated = new Set(
    STEPS.filter((s) => !byId.get(s.id)?.hidden).map((s) => s.id),
  );
  const ownLinks = (id: string): string[] => {
    const s = byId.get(id) ?? STEPS.find((x) => x.id === id);
    return s ? [...s.deps, ...(s.endDeps ?? [])] : [];
  };
  const resolve = (self: string, ids: string[]): string[] => {
    const out = new Set<string>();
    const seen = new Set<string>([self]);
    const queue = [...ids];
    while (queue.length) {
      const id = queue.pop() as string;
      if (seen.has(id)) continue;
      seen.add(id);
      if (dated.has(id)) out.add(id);
      else queue.push(...ownLinks(id));
    }
    return [...out];
  };
  return new Map(
    STEPS.map((s) => {
      const owner = byId.get(s.id)?.owner ?? s.owner;
      // A step removed from Step 1 waits on nothing, so it cannot set a date.
      if (!dated.has(s.id)) return [s.id, { deps: [], endDeps: [], owner }];
      const links = byId.get(s.id) ?? s;
      return [
        s.id,
        {
          deps: resolve(s.id, links.deps),
          endDeps: resolve(s.id, links.endDeps ?? []),
          owner,
        },
      ];
    }),
  );
};

/** A step with nothing on record to wait on. */
const NO_LINKS: { deps: string[]; endDeps: string[] } = { deps: [], endDeps: [] };

const KIND: Record<string, BarKind> = {
  open: "open",
  blocked: "blocked",
  not_started: "planned",
  not_tracked: "planned",
};

export const schedule = (
  b: AmazonBuilding,
  now: Date,
  links: Links = programmeLinks(),
): Record<string, Scheduled> => {
  const out: Record<string, Scheduled> = {};
  const install = installLength(b);
  // Edited links can loop within a chapter; a link back into the loop is skipped.
  const visiting = new Set<string>();
  const visit = (id: string): Scheduled => {
    if (out[id]) return out[id];
    visiting.add(id);
    const state = b.steps[id];
    const own = links.get(id) ?? NO_LINKS;
    const deps = own.deps.filter((d) => !visiting.has(d)).map(visit);
    const ends = own.endDeps.filter((d) => !visiting.has(d)).map(visit);
    visiting.delete(id);
    const waiting = [...deps, ...ends].some(
      (d) => d.kind === "blocked" || d.waiting,
    );
    const days = plannedDays(b, id);
    if (state.state === "done" || state.state === "not_applicable") {
      const end = state.date ? day(state.date) : null;
      out[id] = {
        id,
        kind: "done",
        start: end && days ? addDays(end, -days) : end,
        end,
        waiting: false,
        detail: state.detail,
        noDate: false,
      };
      return out[id];
    }
    let start = latest(soonest(now), ...deps.map((d) => d.end));
    const due = state.due ? day(state.due) : null;
    let end: Date;
    if (id === "install") end = addDays(start, install?.days ?? 0);
    else if (id === "dr3") end = addBusinessDays(start, DR3_BUSINESS_DAYS);
    // After a budget estimate the offer waits on a new application, filed at the soonest.
    else if (id === "gridoffer" && state.estimate_for) end = addMonths(start, OFFER_MONTHS);
    else if (id === "gridoffer") end = due ? latest(due, start) : start;
    else if (id === "gridacc") end = due ? latest(due, start) : addDays(start, days);
    else end = addDays(start, days);
    // Amazon's design inputs still to come are planned to arrive tomorrow, so the design
    // after them never starts on a day they are still missing (Noaa, 18 Oct 2026).
    if (id === "inputs" && designInputs(b).missing.length)
      end = latest(end, addDays(now, INPUTS_ARRIVE_IN_DAYS));
    if (ends.length) end = latest(end, ...ends.map((d) => d.end));
    const kind = KIND[state.state];
    if (kind === "blocked") end = latest(end, soonest(now));
    // Only a step not started yet is planned around the winter; one under way is where it is.
    const moved =
      state.state === "open" || state.state === "blocked"
        ? { start, end }
        : outsideWinter(id, start, end);
    start = moved.start;
    end = moved.end;
    out[id] = {
      id,
      kind,
      start,
      end,
      winter: moved.winter,
      waiting,
      detail: state.detail,
      noDate:
        id === "gridoffer" &&
        state.state === "open" &&
        (!due || state.estimated),
    };
    return out[id];
  };
  STEPS.forEach((s) => visit(s.id));
  return out;
};

/** Why ``schedule`` starts ``id`` when it does: when the step it waits on that finishes
 * last is planned to finish, else tomorrow, the soonest anything not yet done can start;
 * and any move the winter rules made. "" for a step with no planned start. */
export const plannedStartWhy = (
  plan: Record<string, Scheduled>,
  links: Links,
  id: string,
  now: Date,
  title: (id: string) => string,
): string => {
  const start = plan[id]?.start;
  if (!start) return "";
  const still = (d: string): boolean => {
    const end = plan[d]?.end;
    return plan[d]?.kind !== "done" && end != null && end >= soonest(now);
  };
  const after = (links.get(id)?.deps ?? [])
    .filter(still)
    .sort((x, y) => +(plan[y].end as Date) - +(plan[x].end as Date))[0];
  const base = after
    ? `Planned to start when ${title(after)} is planned to finish, ${formatDate(plan[after].end)}.`
    : "Nothing it waits on is still to come, so it is planned to start tomorrow, the soonest anything not yet done can.";
  return [base, plan[id].winter].filter(Boolean).join(" ");
};

/** The years ``b``'s Business Day counts reach with no bank holidays served: design
 * approval's, forward from the design submission and back from when it is needed by. */
export const uncountedFor = (
  plan: Record<string, Scheduled>,
  need: Record<string, Date> | null,
): number[] => uncountedYears([plan.design?.end, plan.dr3?.start, plan.dr3?.end, need?.dr3]);

/** How long ``id`` takes in calendar days when worked back from COD: installation by
 * :func:`installLength`, and the grid offer as an instant. Design approval runs in
 * Business Days, so its calendar length depends on where it falls: :func:`startFor`. */
export const lengthOf = (b: AmazonBuilding, id: string): number => {
  if (id === "install") return installLength(b)?.days ?? 0;
  if (id === "dr3") return DR3_BUSINESS_DAYS * (7 / 5);
  if (id === "gridoffer") return 0;
  return plannedDays(b, id);
};

/** The contract's Installation Start Date: Schedule 1's date or 30 days after design
 * approval, whichever is later (MSPPA definition), with design approval on its recorded
 * date or, where ``approval`` is given, that forecast. */
export const installationStart = (
  b: AmazonBuilding,
  approval: Date | null = null,
): { date: Date; floated: boolean } | null => {
  const scheduled = b.contract_dates?.install ? day(b.contract_dates.install) : null;
  const dr3 = b.steps.dr3;
  const approved = dr3?.state === "done" && dr3.date ? day(dr3.date) : approval;
  const floating = approved ? addDays(approved, 30) : null;
  if (!scheduled) return null;
  return floating && floating > scheduled
    ? { date: floating, floated: true }
    : { date: scheduled, floated: false };
};

/** The days we keep in hand before the Schedule 1 design submission date, so the inputs
 * the design needs arrive with time to use them (Noaa, 2026-10-16). */
export const DEFAULT_BUFFER_DAYS = 30;

/** A Schedule 1 deadline as the date a step must meet: the design submitted, the
 * landlord's approval in, by their dates (finish), and installation started by its date
 * (start). */
export interface ContractDeadline {
  date: Date;
  label: string;
  /** The step must start by it, not finish. */
  start: boolean;
}

export const contractDeadlines = (
  b: AmazonBuilding,
  buffer: number,
): Record<string, ContractDeadline> => {
  const dates = b.contract_dates;
  const out: Record<string, ContractDeadline> = {};
  if (!dates) return out;
  if (dates.design) {
    out.design = {
      date: addDays(day(dates.design), -buffer),
      label: buffer
        ? `Schedule 1 design submission, less ${buffer} days in hand`
        : "Schedule 1 design submission",
      start: false,
    };
  }
  if (dates.llsignoff) {
    out.llsignoff = {
      date: day(dates.llsignoff),
      label: "Schedule 1 landlord approval",
      start: false,
    };
  }
  const installStart = installationStart(b);
  if (installStart) {
    out.install = {
      date: installStart.date,
      label: installStart.floated
        ? "Installation Start Date: 30 days after design approval, later than Schedule 1's"
        : "Schedule 1 installation start",
      start: true,
    };
  }
  return out;
};

/** The later step that sets a step's needed-by date: it must finish before ``next``
 * starts (``next`` takes ``days``), or, where ``next`` only waits on it to finish, by the
 * time ``next`` finishes. ``contract`` names a Schedule 1 deadline that sets it instead:
 * the step's own, or (with ``next``) the date ``next`` must start by. */
export interface NeededLink {
  next: string;
  days: number;
  toFinish: boolean;
  contract?: ContractDeadline;
}

/** The latest a step can finish, worked back, once the winter rules move it earlier: a
 * commissioning window that would touch the winter ends before 1 October, and an install
 * whose crane mobilisation or whole window would fall in it finishes early enough to avoid
 * that. */
const beforeWinter = (id: string, by: Date, length: number): Date => {
  const start = addDays(by, -length);
  if (id === "commission") {
    const w = winterTouched(start, by);
    return w ? addDays(w[0], -1) : by;
  }
  if (id === "install") {
    const w = winterOf(start);
    const cranesBy = new Date(Date.UTC(w[0].getUTCFullYear(), 9, 14));
    if (inside(start, [w[0], cranesBy]) || (inside(start, w) && inside(by, w))) {
      return addDays(by, -(daysBetween(addDays(w[0], -1), start)));
    }
  }
  return by;
};

/** When we delivered what a step of the other side's answers: the latest of its own send
 * (its date while the record says it waits on them) and the days the steps of ours it
 * waits on were done. Owners are Step 1's, as edited there. */
export interface Delivered {
  /** Our step whose completion is the delivery, or the step itself for its own send. */
  from: string;
  on: Date;
}

const deliveredFor = (b: AmazonBuilding, links: Links, id: string): Delivered | null => {
  const step = b.steps[id];
  const own = links.get(id);
  if (!own || !["amazon", "third"].includes(own.owner)) return null;
  if (!step || step.state === "done" || step.state === "not_applicable") return null;
  const dates: Delivered[] = own.deps
    .filter((d) => links.get(d)?.owner === "inrange")
    .filter((d) => b.steps[d]?.state === "done" && b.steps[d].date)
    .map((d) => ({ from: d, on: day(b.steps[d].date) }));
  // Only a date the record gives as our send: one under way, now waiting on them, held up
  // by an item or not. A refusal, an arrangement or a repair dates a step too.
  const underWay = (step.own_state ?? step.state) === "open";
  if (underWay && step.waits_on === "them" && step.date) {
    dates.push({ from: id, on: day(step.date) });
  }
  return dates.sort((x, y) => y.on.getTime() - x.on.getTime())[0] ?? null;
};

/** The soonest the other side can be expected to finish a step it owes: the time the
 * step takes from when we delivered what it answers, and never the day of the delivery
 * itself, at the earliest the day after (Noaa, 19 Oct 2026). */
const notBefore = (b: AmazonBuilding, id: string, delivered: Delivered): Date =>
  id === "dr3"
    ? addBusinessDays(delivered.on, DR3_BUSINESS_DAYS)
    : addDays(delivered.on, Math.max(1, lengthOf(b, id)));

const workBack = (
  b: AmazonBuilding,
  links: Links,
  buffer: number,
): {
  finish: Record<string, Date>;
  via: Record<string, NeededLink>;
  shown: Record<string, Date>;
  floors: Record<string, Delivered>;
} | null => {
  if (!b.committed_cod) return null;
  const cod = day(b.committed_cod);
  const deadlines = contractDeadlines(b, buffer);
  const finish: Record<string, Date> = {};
  const via: Record<string, NeededLink> = {};
  const visiting = new Set<string>();
  const visit = (id: string): Date => {
    if (finish[id]) return finish[id];
    visiting.add(id);
    let by = cod;
    // A step's own deadline: to finish by it, or to start by it and so finish its length
    // after.
    const own = deadlines[id];
    const ownDays = own?.start ? lengthOf(b, id) : 0;
    if (own && addDays(own.date, ownDays) < by) {
      by = addDays(own.date, ownDays);
      via[id] = { next: "", days: ownDays, toFinish: false, contract: own };
    }
    STEPS.forEach((x) => {
      if (visiting.has(x.id)) return;
      const links_ = links.get(x.id) ?? NO_LINKS;
      if (links_.deps.includes(id)) {
        const finishBy = visit(x.id);
        let start = startFor(b, x.id, finishBy);
        const days = daysBetween(start, finishBy);
        const startBy = deadlines[x.id];
        const contract = startBy?.start && startBy.date < start ? startBy : undefined;
        if (contract) start = contract.date;
        if (start < by) {
          by = start;
          via[id] = { next: x.id, days, toFinish: false, contract };
        }
      }
      if (links_.endDeps.includes(id) && visit(x.id) < by) {
        by = visit(x.id);
        via[id] = { next: x.id, days: 0, toFinish: true };
      }
    });
    visiting.delete(id);
    finish[id] = beforeWinter(id, by, lengthOf(b, id));
    return finish[id];
  };
  STEPS.forEach((s) => visit(s.id));
  // A step of the other side's is never needed before it could have been done after our
  // delivery. Only the date shown moves: the steps before it keep the dates worked back
  // from the deadlines, so a late answer never makes an earlier step look on time.
  const shown = { ...finish };
  const floors: Record<string, Delivered> = {};
  STEPS.forEach((s) => {
    const delivered = deliveredFor(b, links, s.id);
    if (!delivered) return;
    const floor = notBefore(b, s.id, delivered);
    if (floor > finish[s.id]) {
      shown[s.id] = floor;
      floors[s.id] = delivered;
    }
  });
  return { finish, via, shown, floors };
};

/** The latest each step can finish and still hold the committed COD and the Schedule 1
 * deadlines (the design submission ``buffer`` days early), worked back through what waits
 * on what, and for a step the other side owes, never before it could follow our delivery;
 * null where the building has no committed COD. */
export const neededBy = (
  b: AmazonBuilding,
  links: Links = programmeLinks(),
  buffer: number = DEFAULT_BUFFER_DAYS,
): Record<string, Date> | null => workBack(b, links, buffer)?.shown ?? null;

/** One line of why a step is needed by its date, from COD back to the step. */
export interface NeededStep {
  id: string;
  /** The latest it can finish. */
  by: Date;
  /** How long it takes; null for the step being explained and for COD. */
  days: number | null;
  /** It only has to finish by the time the step after it finishes. */
  toFinish: boolean;
  /** The Schedule 1 deadline this line is, at the chain's start in place of COD. */
  contract?: string;
  /** On the step explained: the worked-back date was before we delivered what it answers,
   * so ``by`` is the soonest it could follow that delivery instead. */
  floor?: Delivered & { workedBack: Date };
}

/** The chain that sets ``id``'s needed-by date, COD first: each step the latest it can
 * finish, and how long the step after it takes. Null without a committed COD. */
export const neededByChain = (
  b: AmazonBuilding,
  id: string,
  links: Links = programmeLinks(),
  buffer: number = DEFAULT_BUFFER_DAYS,
): NeededStep[] | null => {
  const worked = workBack(b, links, buffer);
  if (!worked) return null;
  const chain: NeededStep[] = [];
  const seen = new Set<string>();
  let at: string | undefined = id;
  const floored = worked.floors[id];
  let deadline: ContractDeadline | undefined;
  /** How long the chain's last step takes, where its own start deadline ends the chain. */
  let lastDays = 0;
  while (at && !seen.has(at)) {
    seen.add(at);
    chain.push({ id: at, by: worked.finish[at], days: null, toFinish: false });
    const link: NeededLink | undefined = worked.via[at];
    // A step whose own deadline sets it ends the chain at that deadline.
    if (link?.contract && !link.next) {
      deadline = link.contract;
      lastDays = link.days;
      break;
    }
    if (link?.contract) {
      // The next step must start by a deadline: it takes its place in the chain.
      chain.push({
        id: link.next,
        by: addDays(link.contract.date, link.days),
        days: null,
        toFinish: false,
      });
      deadline = link.contract;
      lastDays = link.days;
      break;
    }
    at = link?.next;
  }
  // Each line's length is the step's own; the step being explained has none to show.
  for (let i = 1; i < chain.length; i++) {
    const link = worked.via[chain[i - 1].id];
    chain[i].days = link.days;
    chain[i].toFinish = link.toFinish;
  }
  const last = chain[chain.length - 1];
  if (deadline) {
    chain.push({
      id: "contract",
      by: deadline.start ? addDays(last.by, -lastDays) : last.by,
      days: null,
      toFinish: false,
      contract: deadline.label,
    });
  } else if (last.id !== "cod") {
    // The chain stops at the step nothing later constrains, which finishes by COD itself.
    chain.push({ id: "cod", by: day(b.committed_cod), days: null, toFinish: false });
  }
  if (floored) {
    chain[0] = {
      ...chain[0],
      by: worked.shown[id],
      floor: { ...floored, workedBack: worked.finish[id] },
    };
  }
  return chain.reverse();
};

/** The latest an item can arrive and still hold the dates: its own date where it has one
 * (``due``), else when the step it is for must start (schedule.neededBy worked back from
 * the committed COD); null without one. */
export const itemNeededBy = (
  b: AmazonBuilding,
  n: AmazonNeed,
  need: Record<string, Date> | null,
): Date | null => {
  if (n.due) return day(n.due);
  const step = n.due_step || n.step;
  // Due by a step already done: by the day it was done.
  const done = n.due_step ? b.steps[step] : undefined;
  if (done?.state === "done" && done.date) return day(done.date);
  return need?.[step] ? startFor(b, step, need[step]) : null;
};

/** Why an item is needed by its date, as every page that dates it says. */
export const neededByReason = (n: AmazonNeed, title: (step: string) => string): string =>
  n.due_why ||
  (n.due_step === "sppa"
    ? "Before the SPPA is signed, when Amazon owes it under MSPPA s5.5(b)"
    : `When ${title(n.due_step || n.step)} must start`);

/** Still to come (not received or partially received) and past the date it was needed by. */
export const isLateItem = (
  b: AmazonBuilding,
  n: AmazonNeed,
  need: Record<string, Date> | null,
  now: Date,
): boolean => {
  const by = itemNeededBy(b, n, need);
  return owed(n) && by !== null && by < now;
};

/** The design inputs ``b`` still waits for that are past their needed-by dates: while
 * there are any, Amazon design inputs reads Late. */
export const lateInputs = (
  b: AmazonBuilding,
  need: Record<string, Date> | null,
  now: Date,
): AmazonNeed[] => designInputs(b).missing.filter((n) => isLateItem(b, n, need, now));

/** The earliest date any of ``needs`` is needed by; null where none has one. */
export const earliestNeededBy = (
  b: AmazonBuilding,
  needs: AmazonNeed[],
  need: Record<string, Date> | null,
): Date | null =>
  needs
    .map((n) => itemNeededBy(b, n, need))
    .filter((d): d is Date => d !== null)
    .sort((x, y) => x.getTime() - y.getTime())[0] ?? null;
